import React, { useEffect, useMemo, useState, memo } from 'react';
import { Sidebar } from '../components/Dashboard/Sidebar';
import { TopBar } from '../components/Dashboard/TopBar';
import { SummaryCards } from '../components/Dashboard/SummaryCards';
import { FiltersPanel } from '../components/Dashboard/FiltersPanel';
import { ChartsPanel } from '../components/Dashboard/ChartsPanel';
import { LogTable } from '../components/Dashboard/LogTable';
import { LogEntry, DashboardStats } from '../types';
import { generateInitialLogs, generateMockLog } from '../utils/mockData';
import { Button } from '../components/ui/Button';
import { ChevronLeftIcon, ChevronRightIcon, PlayIcon, PauseIcon } from 'lucide-react';
import { ClientsCatalog } from './ClientsCatalog';
import { StatusCatalog } from './StatusCatalog';
interface DashboardProps {
  onLogout: () => void;
}
export function Dashboard({
  onLogout
}: DashboardProps) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLive, setIsLive] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 15;
  // Filters state
  const [filters, setFilters] = useState({
    clientCode: 'ALL',
    statusCode: 'ALL',
    search: ''
  });
  // Applied filters state (to only filter when "Apply" is clicked)
  const [appliedFilters, setAppliedFilters] = useState({
    clientCode: 'ALL',
    statusCode: 'ALL',
    search: ''
  });
  // Initialize logs
  useEffect(() => {
    setLogs(generateInitialLogs(100));
  }, []);
  // Live updates
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      setLogs(prevLogs => {
        const newLog = generateMockLog();
        // Keep max 500 logs in memory to prevent performance issues
        const updatedLogs = [newLog, ...prevLogs].slice(0, 500);
        // Reset isNew flag on older logs
        return updatedLogs.map((log, index) => ({
          ...log,
          isNew: index === 0
        }));
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [isLive]);
  // Apply filters
  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setCurrentPage(1); // Reset to first page on filter
  };
  const handleResetFilters = () => {
    const reset = {
      clientCode: 'ALL',
      statusCode: 'ALL',
      search: ''
    };
    setFilters(reset);
    setAppliedFilters(reset);
    setCurrentPage(1);
  };
  // Filtered logs
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // Client Code Filter
      if (appliedFilters.clientCode !== 'ALL' && log.clientCode !== appliedFilters.clientCode) return false;
      // Status Code Filter
      if (appliedFilters.statusCode !== 'ALL') {
        const statusType = appliedFilters.statusCode;
        if (statusType === '200' && (log.statusCode < 200 || log.statusCode >= 300)) return false;
        if (statusType === '400' && (log.statusCode < 400 || log.statusCode >= 500)) return false;
        if (statusType === '500' && log.statusCode < 500) return false;
      }
      // Search Filter
      if (appliedFilters.search) {
        const searchLower = appliedFilters.search.toLowerCase();
        return log.message.toLowerCase().includes(searchLower) || log.transactionId.toLowerCase().includes(searchLower);
      }
      return true;
    });
  }, [logs, appliedFilters]);
  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * logsPerPage, currentPage * logsPerPage);
  // Calculate stats based on ALL logs (not just filtered)
  const stats: DashboardStats = useMemo(() => {
    const total = logs.length;
    let success = 0,
      failed = 0,
      pending = 0;
    logs.forEach(log => {
      if (log.statusCode >= 500) failed++;else if (log.statusCode >= 200 && log.statusCode < 300) success++;else pending++;
    });
    return {
      total,
      success,
      failed,
      pending
    };
  }, [logs]);
  return <div className="flex h-screen bg-gray-50 text-foreground overflow-hidden" data-id="element-275">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={onLogout} data-id="element-276" />

      <div className="flex-1 flex flex-col overflow-hidden relative" data-id="element-277">
        {/* Subtle background grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb40_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb40_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none z-0" data-id="element-278"></div>

        <TopBar data-id="element-279" />

        <main className="flex-1 overflow-y-auto p-6 z-10" data-id="element-280">
          <div className="max-w-7xl mx-auto space-y-6" data-id="element-281">
            {/* Header Area */}
            <div className="flex items-center justify-between mb-8" data-id="element-282">
              <div data-id="element-283">
                <h2 className="text-2xl font-bold tracking-tight" data-id="element-284">
                  {activeTab === 'dashboard' || activeTab === 'logs' ? 'Logs en Tiempo Real' : activeTab === 'clients' ? 'Gestión de Clientes' : activeTab === 'status' ? 'Gestión de Códigos de Estado' : 'Panel Principal'}
                </h2>
                <p className="text-muted-foreground mt-1" data-id="element-285">
                  {activeTab === 'dashboard' || activeTab === 'logs' ? 'Monitorea los logs y errores del sistema' : 'Configura y administra los ajustes del sistema'}
                </p>
              </div>
              {(activeTab === 'dashboard' || activeTab === 'logs') && <div className="flex items-center space-x-4" data-id="element-286">
                  <Button variant={isLive ? 'outline' : 'default'} size="sm" onClick={() => setIsLive(!isLive)} className={`transition-all ${isLive ? 'border-green-500/50 text-green-500 hover:bg-green-500/10' : ''}`} data-id="element-287">
                    {isLive ? <>
                        <PauseIcon className="w-4 h-4 mr-2" data-id="element-288" /> Pausar
                      </> : <>
                        <PlayIcon className="w-4 h-4 mr-2" data-id="element-289" /> Reanudar
                      </>}
                  </Button>
                </div>}
            </div>

            {/* Dashboard Content */}
            {(activeTab === 'dashboard' || activeTab === 'logs') && <>
                <SummaryCards stats={stats} data-id="element-290" />

                {activeTab === 'dashboard' && <ChartsPanel logs={logs} data-id="element-291" />}

                <FiltersPanel filters={filters} setFilters={setFilters} onApply={handleApplyFilters} onReset={handleResetFilters} data-id="element-292" />

                <div className="space-y-4" data-id="element-293">
                  <div className="flex items-center justify-between" data-id="element-294">
                    <h3 className="text-lg font-medium" data-id="element-295">Flujo de Logs</h3>
                    <span className="text-sm text-muted-foreground" data-id="element-296">
                      Mostrando {paginatedLogs.length} de {filteredLogs.length}{' '}
                      entradas
                    </span>
                  </div>

                  <LogTable logs={paginatedLogs} data-id="element-297" />

                  {/* Pagination Controls */}
                  {totalPages > 1 && <div className="flex items-center justify-between pt-4 border-t border-border/50" data-id="element-298">
                      <p className="text-sm text-muted-foreground" data-id="element-299">
                        Página {currentPage} de {totalPages}
                      </p>
                      <div className="flex space-x-2" data-id="element-300">
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-id="element-301">
                          <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-302" /> Anterior
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} data-id="element-303">
                          Siguiente{' '}
                          <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-304" />
                        </Button>
                      </div>
                    </div>}
                </div>
              </>}

            {activeTab === 'clients' && <ClientsCatalog data-id="element-305" />}

            {activeTab === 'status' && <StatusCatalog data-id="element-306" />}

            {/* Placeholder for other tabs */}
            {activeTab === 'filters' && <div className="flex items-center justify-center h-64 border border-dashed border-border rounded-lg bg-card/50" data-id="element-307">
                <p className="text-muted-foreground" data-id="element-308">
                  Advanced Filters Configuration (Coming Soon)
                </p>
              </div>}

            {activeTab === 'settings' && <div className="flex items-center justify-center h-64 border border-dashed border-border rounded-lg bg-card/50" data-id="element-309">
                <p className="text-muted-foreground" data-id="element-310">
                  Dashboard Settings (Coming Soon)
                </p>
              </div>}
          </div>
        </main>
      </div>
    </div>;
}